import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Brain, ArrowsClockwise, TrendUp, Shield, Lightning, Robot } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { toast } from 'sonner';
import { Button } from './ui/button';

const RISK_META = {
  Conservative: { color: 'text-emerald-300', bg: 'bg-emerald-500/15', border: 'border-emerald-400/30', icon: Shield },
  Moderate:     { color: 'text-yellow-300',  bg: 'bg-yellow-500/15',  border: 'border-yellow-400/30',  icon: TrendUp },
  Aggressive:   { color: 'text-pink-300',    bg: 'bg-pink-500/15',    border: 'border-pink-400/30',    icon: Lightning },
};

const SectorPill = ({ sector }) => (
  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-[10px] uppercase tracking-wide text-zinc-300">
    {sector}
  </span>
);

const SuggestionCard = ({ suggestion, onClick }) => (
  <button
    onClick={onClick}
    className="group text-left w-full p-4 rounded-xl bg-white/[0.03] border border-white/10 hover:border-cyan-400/40 hover:bg-cyan-400/[0.04] transition-all"
    data-testid={`ai-suggestion-${suggestion.symbol}`}
  >
    <div className="flex items-center justify-between mb-1.5">
      <div className="flex items-center gap-2">
        <span className="font-unbounded font-bold text-white group-hover:text-cyan-200 transition-colors">
          {suggestion.symbol}
        </span>
        <SectorPill sector={suggestion.sector} />
      </div>
      <span className="text-xs text-cyan-300 opacity-0 group-hover:opacity-100 transition-opacity">View →</span>
    </div>
    <p className="text-xs text-zinc-400 line-clamp-1 mb-2">{suggestion.name}</p>
    <p className="text-sm text-zinc-200 leading-relaxed">{suggestion.thesis}</p>
  </button>
);

const AIAnalystPanel = ({ analysis, onRefreshed }) => {
  const navigate = useNavigate();
  const [refreshing, setRefreshing] = useState(false);

  if (!analysis) return null;
  const risk = analysis.risk_profile?.label || 'Moderate';
  const meta = RISK_META[risk] || RISK_META.Moderate;
  const Icon = meta.icon;

  const refresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const res = await api.post('/ai-analyst/refresh');
      toast.success('Analysis refreshed');
      onRefreshed?.(res.data.analysis);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not refresh analysis');
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden rounded-2xl border border-cyan-400/30 bg-gradient-to-br from-cyan-950/30 via-zinc-950 to-zinc-950 p-5 sm:p-6 mb-6"
      data-testid="ai-analyst-panel"
    >
      <div className="pointer-events-none absolute -top-24 -right-12 w-72 h-72 rounded-full bg-cyan-400/10 blur-3xl" />

      {/* Header */}
      <div className="relative flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center shadow-lg shadow-cyan-500/30 flex-shrink-0">
            <Brain size={22} weight="fill" className="text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-base sm:text-lg font-unbounded font-bold text-white">AI Analyst</h3>
              <span className="text-[10px] uppercase tracking-wider text-cyan-300 font-bold bg-cyan-500/15 px-1.5 py-0.5 rounded-full">Live</span>
            </div>
            <p className="text-xs text-zinc-400">Focused on {(analysis.interests || []).join(', ') || 'All sectors'}</p>
          </div>
        </div>
        <Button
          onClick={refresh}
          disabled={refreshing}
          variant="ghost"
          size="sm"
          className="border border-white/10 hover:border-cyan-400/40 text-zinc-300"
          data-testid="ai-analyst-refresh-button"
        >
          <ArrowsClockwise size={14} className={refreshing ? 'animate-spin mr-1.5' : 'mr-1.5'} />
          {refreshing ? 'Refreshing' : 'Refresh'}
        </Button>
      </div>

      {/* Headline */}
      <p className="relative text-zinc-100 font-semibold leading-relaxed mb-4">
        {analysis.headline}
      </p>

      {/* Risk + Review row */}
      <div className="relative grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
        <div className={`rounded-xl border ${meta.border} ${meta.bg} p-4`} data-testid="ai-analyst-risk-card">
          <div className="flex items-center gap-1.5 mb-1">
            <Icon size={14} weight="fill" className={meta.color} />
            <span className="text-[10px] uppercase tracking-wide text-zinc-400">Your Risk</span>
          </div>
          <p className={`text-lg font-unbounded font-bold ${meta.color}`}>{risk}</p>
          <p className="text-xs text-zinc-400 mt-1">{analysis.risk_summary}</p>
        </div>
        <div className="md:col-span-2 rounded-xl border border-white/10 bg-white/[0.03] p-4">
          <p className="text-[10px] uppercase tracking-wide text-zinc-400 mb-1">Portfolio Review</p>
          <p className="text-sm text-zinc-200 leading-relaxed">{analysis.portfolio_review}</p>
        </div>
      </div>

      {/* Suggestions */}
      <div className="relative">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs uppercase tracking-wider text-cyan-300 font-bold">Picks for you</p>
          <span className="text-[10px] text-zinc-500">{(analysis.suggestions || []).length} ideas</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(analysis.suggestions || []).map((s) => (
            <SuggestionCard
              key={s.symbol}
              suggestion={s}
              onClick={() => navigate(`/market/${s.symbol}`)}
            />
          ))}
        </div>
      </div>

      {/* Next step */}
      {analysis.next_step && (
        <div className="relative mt-4 p-3 rounded-xl bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-400/20">
          <div className="flex items-start gap-2">
            <Robot size={16} weight="fill" className="text-cyan-300 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-zinc-100"><span className="font-semibold text-cyan-200">Next step:</span> {analysis.next_step}</p>
          </div>
        </div>
      )}

      {/* Disclaimer */}
      <p className="relative text-[10px] text-zinc-500 mt-3 italic">
        {analysis.disclaimer || 'Simulation-based educational analysis only — not real financial advice.'}
      </p>
    </motion.section>
  );
};

export default AIAnalystPanel;
